import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';

import { useTranslate } from '../../../../i18n/useTranslationModules';
import { AccordionContent, ContentEditors, FeatureFlagsEnum, ModuleMap, PageModule, PageModuleType } from '../../../../types/working-model';
import { useScreenMediaQuery } from '../../../../utils/use-screen-media-query';
import { getDefaultModuleAlignment, getTextValue } from '../../../../utils/utils';
import { TypographyItem } from '../../../general-ui/typography-item/typography-item';
import { ParamsProps } from '../../live-event';
import { getStylingOverrides } from '../../utils';
import AccordionColumn from './accordion-column';
import '../../../../scss/live-event/base/modules/accordion.scss';
import RenderEditorDescription from '../../../general-ui/editable-text/render-editor-description';
import { useTypedSelector } from '../../../../store/reducers/use-typed-selector';
import useTranslationRoute from '../../hooks/use-translation-route';
import { UniqueIdentifier } from '@dnd-kit/core';

interface IAccordionProps {
	module: PageModule;
	template: string;
}

const Accordion = React.forwardRef<HTMLDivElement, IAccordionProps>((props, ref) => {
	const { module, template } = props;

	const eventBundleContentEditor = useTypedSelector(state => state.LiveEventReducer.eventBundle?.settings?.content_editor);
	const workingEventContentEditor = useTypedSelector(state => state.CreateEventReducer.workingEvent?.settings?.content_editor);
	const contentEditor = eventBundleContentEditor || workingEventContentEditor;

	const featureFlags = useTypedSelector(state => state.FeatureFlagsReducer.featureFlags);
	const accordionModuleFlagOn = featureFlags[FeatureFlagsEnum.accordion_module];

	const params: ParamsProps = useParams();
	const { isLessThan640: isMobile, isLessThan1024: isTablet } = useScreenMediaQuery();

	const translationRoute = useTranslationRoute();
	const { t } = useTranslate([translationRoute, "homepage"]);

	const [activeItem, setActiveItem] = useState<Record<UniqueIdentifier, UniqueIdentifier>>({});

	const language = params.language || 'en';
	const content: AccordionContent = module.content;
	const styling_overrides = module.styling_overrides;
	const vertical = isMobile || isTablet;
	const containers = content?.container_order;

	const stylingOverrides = getStylingOverrides(styling_overrides);

	const {
		backgroundColor,
		backgroundImg,
		customHeadingOverrides,
		titleOverrides,
		descriptionOverrides
	} = stylingOverrides;

	const { custom_heading, description, title } = getTextValue(content, language);

	useEffect(() => {
		// if a window goes between vertical columns, reset open items
		setActiveItem({});
	}, [vertical]);

	const renderColumns = () => {
		if (!module.id) return null;

		// render one column for vertical layouts
		if (vertical) {
			const allItems = content.container_order.map(columnId => {
				const columnItems = content.columns[Number(columnId)];
				return [...columnItems];
			}).flat();

			return (
				<AccordionColumn column={{ columnId: 1, columnItems: allItems }} vertical={vertical} activeItemState={[activeItem, setActiveItem]} itemTabIndex={1} moduleId={module.id} />
			);
		}

		return content.container_order.map((columnId, idx) => {
			if (!module.id) return null;

			const columnItems = content.columns[Number(columnId)];
			return (
				<AccordionColumn key={columnId} column={{ columnId, columnItems }} vertical={vertical} activeItemState={[activeItem, setActiveItem]} itemTabIndex={idx + 1} moduleId={module.id} />
			);
		});
	};

	if (!content || !accordionModuleFlagOn) return null;
	return (
		<section
			id={ModuleMap[PageModuleType.accordion]}
			className={classNames(
				'accordion',
				template,
				backgroundColor
			)}
			ref={ref}
		>
			<div className="module-container" style={backgroundImg}>
				<TypographyItem className={classNames('evt-caption-1 accordion-custom_heading', customHeadingOverrides)} tagName="p">
					{t(`${translationRoute}:accordion.${module.id}.custom_heading`, custom_heading)}
				</TypographyItem>
				<TypographyItem className={classNames('evt-heading-2 accordion-title', titleOverrides)} tagName="h2">
					{t(`${translationRoute}:accordion.${module.id}.title`, title)}
				</TypographyItem>
				<TypographyItem className={classNames('evt-body-text-1 accordion-description')}>
					<RenderEditorDescription
						description={t(`${translationRoute}:accordion.${module.id}.description`, description)}
						descriptionOverrides={classNames(descriptionOverrides)}
						useNew={contentEditor === ContentEditors.quill}
						defaultTextAlignment={getDefaultModuleAlignment(module, template)}
					/>
				</TypographyItem>

				<div className={classNames('accordion-container', { vertical }, { 'single-column': containers?.length === 1 })}>
					{renderColumns()}
				</div>

			</div>
		</section>
	);
});

Accordion.displayName = 'Accordion';

export default Accordion;
